import { useEffect, useState } from "react";
import { getToken, setToken } from "./lib/api.js";
import { Brief } from "./pages/Brief.js";
import { Pipeline } from "./pages/Pipeline.js";
import { Approvals } from "./pages/Approvals.js";
import { Support } from "./pages/Support.js";
import { AgentOps } from "./pages/AgentOps.js";
import { Revenue } from "./pages/Revenue.js";
import { AppStore } from "./pages/AppStore.js";
import { Goals } from "./pages/Goals.js";
import { Controls } from "./pages/Controls.js";
import { Studio } from "./pages/Studio.js";

const PAGES = [
  { id: "brief", label: "Brief" },
  { id: "pipeline", label: "Pipeline" },
  { id: "approvals", label: "Approvals" },
  { id: "support", label: "Support" },
  { id: "studio", label: "Studio" },
  { id: "agentops", label: "Agent Ops" },
  { id: "revenue", label: "Revenue" },
  { id: "appstore", label: "App Store" },
  { id: "controls", label: "Controls" },
  { id: "goals", label: "Goals" },
] as const;

type PageId = (typeof PAGES)[number]["id"];

function pageFromHash(): PageId {
  const h = window.location.hash.replace(/^#\/?/, "");
  return (PAGES.some((p) => p.id === h) ? h : "brief") as PageId;
}

export function App() {
  const [authed, setAuthed] = useState(Boolean(getToken()));
  const [page, setPage] = useState<PageId>(pageFromHash());

  useEffect(() => {
    const onHash = () => setPage(pageFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const nav = (id: string) => { window.location.hash = `/${id}`; };

  if (!authed) return <TokenGate onDone={() => setAuthed(true)} />;

  return (
    <div className="shell">
      <aside className="rail">
        <div className="brand">
          <div className="word">Turbo<em>Proc</em></div>
          <div className="sub">App Growth Autopilot</div>
        </div>
        <nav className="nav">
          {PAGES.map((p, i) => (
            <a key={p.id} href={`#/${p.id}`} className={page === p.id ? "active" : ""}>
              <span className="idx">{String(i + 1).padStart(2, "0")}</span>
              {p.label}
            </a>
          ))}
        </nav>
        <div className="rail-foot">
          <span>autopilot · phase 3</span>
          <span>more throughput by the next shift</span>
        </div>
      </aside>

      <main className="main" key={page}>
        {page === "brief" && <Brief nav={nav} />}
        {page === "pipeline" && <Pipeline />}
        {page === "approvals" && <Approvals />}
        {page === "support" && <Support />}
        {page === "studio" && <Studio />}
        {page === "agentops" && <AgentOps />}
        {page === "revenue" && <Revenue />}
        {page === "appstore" && <AppStore />}
        {page === "controls" && <Controls />}
        {page === "goals" && <Goals />}
      </main>

      <nav className="mobilebar">
        {PAGES.map((p) => (
          <a key={p.id} href={`#/${p.id}`} className={page === p.id ? "active" : ""}>{p.label}</a>
        ))}
      </nav>
    </div>
  );
}

function TokenGate(props: { onDone: () => void }) {
  const [value, setValue] = useState("");
  return (
    <div className="gate">
      <div className="panel">
        <h2>Operator authentication</h2>
        <p className="dim" style={{ fontSize: 12.5 }}>
          Paste the dashboard token (the <span className="mono">DASHBOARD_API_TOKEN</span> secret).
          It is stored only in this browser.
        </p>
        <div className="row">
          <input
            style={{ flex: 1 }}
            type="password"
            placeholder="bearer token"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && value.trim().length >= 16) { setToken(value); props.onDone(); } }}
          />
          <button className="btn primary" disabled={value.trim().length < 16} onClick={() => { setToken(value); props.onDone(); }}>
            Engage
          </button>
        </div>
      </div>
    </div>
  );
}
